/* eslint-disable class-methods-use-this,max-classes-per-file */

'use strict';

define('vb/private/events/undeclaredEventBehavior',[
  'vb/private/constants',
  'vb/private/events/eventBehavior',
], (Constants, EventBehavior) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/page').default} Page
   * @typedef {import('vb/private/events/eventModel').default} EventModel
   */

  const NAMESPACES = [
    Constants.GLOBAL_PREFIX,
    Constants.APPLICATION_PREFIX,
    Constants.FLOW_PREFIX,
    Constants.PAGE_PREFIX,
    Constants.LAYOUT_PREFIX,
    Constants.FRAGMENT_PREFIX,
  ];

  /**
   * UndeclaredEventBehavior
   *
   * This class represents the behavior for events that are not declared in an "events" section.
   * These are the original style of events (previously called 'custom' events) and the built-in 'vbXXX' events.
   *
   * When using fireCustomEventAction with undeclared events:
   * - Start bubbling at the container that calls the action.
   * - No specific behavior: just call all listeners, don't wait for Promises, and don't return results.
   * - Call event listeners without waiting for any responses.
   */
  class UndeclaredEventBehavior extends EventBehavior {
    /**
     * Determines the starting container for the event.
     *
     * By default, it starts from the provided container unless the event name is namespaced,
     * in which case it returns null. This method is used to decide the container from which
     * the event bubbling should begin.
     *
     * @param {Container} container - The container to potentially start from.
     * @returns {Container|null} - The starting container if not namespaced, otherwise null.
     */
    startingContainer(container) {
      return UndeclaredEventBehavior.isNamespaced(this.name) ? null : container;
    }

    /**
     * Checks if the provided event name is namespaced.
     * An event name is considered namespaced if it contains a colon (':') and the prefix
     * (the part before the colon) matches one of the known namespaces.
     *
     * @param {String} name - The event name to check.
     * @returns {boolean} - Returns true if the event name is namespaced, otherwise false.
     */
    // eslint-disable-next-line class-methods-use-this
    static isNamespaced(name) {
      const parts = (name || '').split(':');
      if (parts.length === 2) {
        const prefixParts = parts[0].split('/'); // get the last part
        return NAMESPACES.indexOf(prefixParts[prefixParts.length - 1]) >= 0;
      }
      return false;
    }

    /**
     * Determines whether an undeclared event can be triggered by anyone.
     * This method overrides the default implementation to always return true,
     * indicating that undeclared events can be triggered by any entity.
     *
     * @param {Container} container - The container that is attempting to trigger the event.
     * @returns {boolean} - Returns true, indicating that any entity can trigger an undeclared event.
     * @overrides
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    triggerableBy(container) {
      return true;
    }

    /**
     * Determines if an undeclared event is listenable by any entity.
     *
     * This method checks whether the event name is namespaced and returns a boolean
     * indicating whether the event can be listened to. Specifically, undeclared events
     * are always listenable unless the event name is namespaced.
     *
     * @param {Container|Page} container - The container that is attempting to listen to the event.
     * @returns {boolean} - Returns `true` if the event is not namespaced and can be listened to,
     *                      otherwise `false`.
     * @overrides
     */
    // eslint-disable-next-line no-unused-vars
    listenableBy(container) {
      return !UndeclaredEventBehavior.isNamespaced(this.name);
    }

    /**
     * Curried listeners for undeclared events.
     *
     * This method curates the event listeners by creating a list of function wrappers for each listener.
     * Unlike typical event processing, this method does not process extensions and only directly invokes
     * the specified event on the provided container.
     *
     * @param {Container} container - The container that owns the event being invoked.
     * @param {EventModel} eventModel - The overall event model describing event metadata (not used in this context).
     * @param {Object} eventWrapper - An object containing details about the event.
     * @param {String} eventWrapper.name - The name of the event being invoked.
     * @param {Container} eventWrapper.origin - The container that fired the event.
     * @param {*} eventWrapper.payload - The payload of the event.
     * @param {*} eventWrapper.coercedPayload - The payload coerced to the defined payload type.
     * @param {EventModel} eventWrapper.eventModel - The EventModel defining the event.
     * @returns {Array<{ container: Container, fnc: function }>} - An array of objects containing
     * the container and its corresponding listener function.
     * @overrides
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    curryListeners(container, eventModel, eventWrapper) {
      const functionWrappers = [];
      const fnc = container.invokeEvent.bind(container, eventWrapper.name, eventWrapper.coercedPayload);
      functionWrappers.push({
        container,
        fnc,
      });
      return functionWrappers;
    }
  }

  return UndeclaredEventBehavior;
});

